import Icon from 'components/atoms/Icon';
import UserWithLink from 'components/atoms/UserWithLink/UserWithLink';
import { InfoItem } from 'components/molecules/InfoItem/InfoItem';
import OpenCompanyAppButton from 'components/molecules/OpenCompanyAppButton/OpenCompanyAppButton';
import PolicyDependentButton from 'components/molecules/PolicyDependentButtons/PolicyDependentButton';
import { emptyDisplayValue } from 'constants/common';
import { companyAppRoutes } from 'constants/navigation';
import { PROJECT_SOURCE_LABELS } from 'constants/project';
import {
  ActorRole,
  ProjectAction,
  ProjectCollaborator,
  ProjectPageProjectFragment,
  ProjectSource,
  useRoleArtefactsGetJobDescriptionUrlLazyQuery,
  useRoleArtefactsMeetingFindOneQuery,
  useRoleArtefactsRoleFindOneQuery,
} from 'generated/graphql';
import { copyTextToClipboard } from 'helpers/clipboard';
import {
  collaboratorsByRole,
  formatCamelCase,
  formatProjectDate,
} from 'helpers/project';
import { downloadFileFromUrl } from 'helpers/uploadService';
import { useProjectActionsValidation } from 'hooks/useProjectPolicyActions/useProjectPolicyActions';
import { IProjectPath } from 'interfaces/routes';
import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { getSupportedLocaleMetadata, SupportedLocale } from '@sz/l10n';
import {
  Button,
  InlineNotification,
  NotificationType,
  TNotification,
  Tooltip,
  useNotification,
} from '@spotted-zebra-uk/ui-components';
import JobDescriptionValue from './components/JobDescriptionValue/JobDescriptionValue';

interface IProjectInfo {
  project: ProjectPageProjectFragment;
  collaborators?: Array<ProjectCollaborator> | null;
  onEdit: () => void;
}

const ProjectCollaboratorItem = ({
  label,
  collaborators,
}: {
  label: string;
  collaborators: Array<ProjectCollaborator>;
}) => {
  return collaborators.length ? (
    <div className="project-info__item">
      <div className="project-info__label">{label}</div>
      <div className="project-info__value">
        {collaborators.map((collaborator, index) => (
          <Tooltip
            key={collaborator.id}
            content={
              collaborator.user?.email ||
              collaborator.group?.membersPreview
                .map((user) => `${user.firstName} ${user.lastName}`)
                .join(', ')
                .concat(
                  collaborator.group?.membersCount >
                    (collaborator.group?.membersPreview.length || 0)
                    ? ` + ${
                        collaborator.group?.membersCount -
                        collaborator.group?.membersPreview.length
                      } more`
                    : '',
                )
            }
          >
            <span className="project-info__tooltip-content">
              {collaborator.user?.firstName}
              {collaborator.group?.name} {collaborator.user?.lastName}
              {collaborator.group
                ? `(${collaborator.group?.membersCount})`
                : ''}
              {index === collaborators.length - 1 ? '' : ', '}
            </span>
          </Tooltip>
        ))}
      </div>
    </div>
  ) : null;
};

export const ProjectOverviewInfo: FC<IProjectInfo> = ({
  project,
  collaborators,
  onEdit,
}) => {
  const { projectId } = useParams() as unknown as IProjectPath;

  const generalSectionInfo = [
    { label: 'Company', value: project.company?.name },
    {
      label: 'Product solution',
      value: formatCamelCase(project.productSolution),
    },
    {
      label: 'Product module type',
      value: formatCamelCase(project.moduleType),
    },
  ];

  const unassigned = collaborators?.filter(
    (collaborator) => !collaborator.actorRole,
  );
  const byRole = collaboratorsByRole(collaborators || []);

  const handleOpenProjectOnCompanyApp = () => {
    window.open(
      companyAppRoutes.role.url(projectId, project.company?.id),
      '_blank',
      'noopener,noreferrer',
    );
  };

  const { handleMsgType } = useNotification();

  const { data: roleData } = useRoleArtefactsRoleFindOneQuery({
    variables: { id: project.id },
    skip: project.source !== ProjectSource.RoleKickoff || !project.id,
  });

  const { data: meetingData } = useRoleArtefactsMeetingFindOneQuery({
    variables: { id: roleData?.RoleWizardRoleFindOne?.meetingId ?? '' },
    skip:
      project.source !== ProjectSource.RoleKickoff ||
      !roleData?.RoleWizardRoleFindOne?.meetingId,
  });
  const meetingDetails = meetingData?.MeetingFindOne;
  const jobDescriptionFileName =
    roleData?.RoleWizardRoleFindOne?.jobDescriptionFileName;
  const jobDescriptionProcessingStatus =
    roleData?.RoleWizardRoleFindOne?.jobDescriptionProcessingStatus;

  const [getJobDescriptionUrl, { loading: jdDownloadLoading }] =
    useRoleArtefactsGetJobDescriptionUrlLazyQuery({
      variables: { roleId: project.id },
      fetchPolicy: 'network-only',
    });

  const handleJDDownload = () => {
    getJobDescriptionUrl().then((result) => {
      const url = result.data?.RoleWizardGetJobDescriptionUrl;
      if (!url) {
        handleMsgType({
          type: TNotification.error,
          title: 'Job description could not be downloaded.',
          message: 'Please try again later.',
          dismissable: true,
        });
        return;
      }
      downloadFileFromUrl(
        url,
        jobDescriptionFileName || 'job-description',
        () =>
          handleMsgType({
            type: TNotification.success,
            title: 'Download started successfully',
            dismissable: true,
          }),
        () =>
          handleMsgType({
            type: TNotification.error,
            title: 'Job description could not be downloaded.',
            message: 'Please try again later.',
            dismissable: true,
          }),
      );
    });
  };

  const handleCopyMeetingSubId = () => {
    if (!meetingDetails?.id) return;
    copyTextToClipboard(meetingDetails.id);
    handleMsgType({
      type: TNotification.success,
      title: 'Meeting subId copied to clipboard',
      dismissable: true,
    });
  };

  return (
    <div>
      <div className="project-header">
        <div className="project-header__left">
          <div className="project-header__title">Role overview</div>
        </div>

        <div className="project-header__right">
          <OpenCompanyAppButton onClick={handleOpenProjectOnCompanyApp} />
          <Button onClick={onEdit} size="medium">
            Edit
          </Button>
        </div>
      </div>

      <div className="project-info project-info__indent">
        <div className="project-info__details">
          <div className="project-info__details-row">
            <InfoItem
              label="Source"
              value={PROJECT_SOURCE_LABELS[project.source]}
            />
          </div>
          <div className="project-info__details-row">
            <InfoItem
              label="Created at"
              value={formatProjectDate(project.createdAt)}
            />
            <InfoItem
              label="Created by"
              value={
                project.creator?.firstName && project.creator?.lastName ? (
                  <UserWithLink user={project.creator} />
                ) : (
                  emptyDisplayValue
                )
              }
            />
          </div>
          <div className="project-info__details-row">
            <InfoItem
              label="Edited at"
              value={formatProjectDate(project.updatedAt)}
            />
            <InfoItem
              label="Edited by"
              value={
                project.updatedBy?.firstName && project.updatedBy?.lastName ? (
                  <UserWithLink user={project.updatedBy} />
                ) : (
                  emptyDisplayValue
                )
              }
            />
          </div>
          <div className="project-info__details-row">
            <InfoItem
              label="Published at"
              value={
                project.publishedAt
                  ? formatProjectDate(project.publishedAt)
                  : emptyDisplayValue
              }
            />
            <InfoItem
              label="Published by"
              value={
                project.publishedBy?.firstName &&
                project.publishedBy?.lastName ? (
                  <UserWithLink user={project.publishedBy} />
                ) : (
                  emptyDisplayValue
                )
              }
            />
          </div>
        </div>
        <h3 className="project-info__title">General</h3>
        {generalSectionInfo.map(({ label, value }) => (
          <InfoItem key={label} label={label} value={value} />
        ))}

        <h3 className="project-info__title">Hiring team</h3>
        {collaborators && !collaborators.length ? (
          <InlineNotification
            notificationType={NotificationType.WARNING}
            className="project-info__warning"
          >
            <p>
              Add users to the hiring team to make sure they can access the
              role.
            </p>
          </InlineNotification>
        ) : null}

        {unassigned?.length ? (
          <InlineNotification
            title="Missing Information"
            notificationType={NotificationType.WARNING}
            className="project-info__warning"
          >
            <p>
              Edit the role overview to tag hiring roles for these hiring team
              members:
            </p>
            <p className="project-info__emphasis">
              <>
                {unassigned.map((collaborator, index) => (
                  <Tooltip
                    key={collaborator.id}
                    content={collaborator.user?.email}
                  >
                    <span className="project-info__tooltip-content">
                      {collaborator.user?.firstName}
                      {collaborator.group?.name} {collaborator.user?.lastName}
                      {index === unassigned.length - 1 ? '' : ', '}
                    </span>
                  </Tooltip>
                ))}
              </>
            </p>
          </InlineNotification>
        ) : null}

        <ProjectCollaboratorItem
          label="Hiring manager"
          collaborators={byRole[ActorRole.HiringManager] || []}
        />
        <ProjectCollaboratorItem
          label="Recruiter"
          collaborators={
            byRole[ActorRole.RecruiterOrTalentAcquisitionPartner] || []
          }
        />
        <ProjectCollaboratorItem
          label="Interviewer"
          collaborators={byRole[ActorRole.Interviewer] || []}
        />
        <ProjectCollaboratorItem
          label="Reskilling team member"
          collaborators={byRole[ActorRole.ReskillingTeamMember] || []}
        />
        <ProjectCollaboratorItem
          label="Line manager"
          collaborators={byRole[ActorRole.LineManager] || []}
        />
        <ProjectCollaboratorItem
          label="Early careers team member"
          collaborators={byRole[ActorRole.EarlyCareerTeamMember] || []}
        />
        <ProjectCollaboratorItem
          label="Head of People/Talent"
          collaborators={byRole[ActorRole.HeadOfPeopleOrTalent] || []}
        />
        {project.source === ProjectSource.RoleKickoff && (
          <>
            <h3 className="project-info__title">Role artefacts</h3>
            <InfoItem
              label="Job description"
              value={
                jobDescriptionFileName ? (
                  <span className="project-info__value-with-action">
                    <JobDescriptionValue
                      fileName={jobDescriptionFileName}
                      jobDescriptionProcessingStatus={
                        jobDescriptionProcessingStatus
                      }
                      onClick={handleJDDownload}
                      loading={jdDownloadLoading}
                    />
                  </span>
                ) : (
                  emptyDisplayValue
                )
              }
            />
            <InfoItem
              label="Intake meeting"
              value={
                meetingDetails ? (
                  <span className="project-info__value-with-action">
                    {meetingDetails.name}
                    <Button
                      size="small"
                      variant="tertiary"
                      leftIcon={<Icon icon="copy" />}
                      onClick={handleCopyMeetingSubId}
                      data-testid="project-info__copy-meeting-subid-button"
                    >
                      Copy meeting subId
                    </Button>
                  </span>
                ) : (
                  emptyDisplayValue
                )
              }
            />
          </>
        )}
      </div>
    </div>
  );
};

export const ProjectLanguageInfo: FC<IProjectInfo> = ({ project, onEdit }) => {
  const { projectPolicyActions } = useProjectActionsValidation({
    projectId: project.id,
  });

  return (
    <div>
      <div className="project-header">
        <div className="project-header__left">
          <div className="project-header__sub-title">Languages</div>
        </div>

        <div className="project-header__right">
          <PolicyDependentButton
            id="project-languages"
            projectStatus={project.status}
            disabled={
              !projectPolicyActions[ProjectAction.UpdateProjectLanguages]
            }
            renderItem={(disabled) => (
              <Button onClick={onEdit} size="medium" disabled={disabled}>
                Edit
              </Button>
            )}
          />
        </div>
      </div>

      <div className="project-info">
        <div className="project-info__item">
          <div className="project-info__label">Selected</div>
          <div className="project-info__value">
            {project.locales
              .map(
                (locale) =>
                  getSupportedLocaleMetadata(SupportedLocale[locale]).name
                    .english,
              )
              .join(', ')}
          </div>
        </div>
      </div>
    </div>
  );
};

export const ProjectReportsInfo: FC = () => {
  return (
    <div>
      <div className="project-header">
        <div className="project-header__left">
          <div className="project-header__sub-title">Reports</div>
        </div>
      </div>
    </div>
  );
};

export default ProjectOverviewInfo;
